// Site map: routes, top-bar groups, section sub-tabs and drawer groups.
// Top level: Summary · Markets (Feed, Wall Street, Social Radar) · Calendar ·
// My Assets (Portfolio, Signals, Price Alerts, Large Transfers) · Orca AI.
// Titles come from the dictionary (nav:page.<id>) in the current language.
import i18n from '@/i18n';

type PageId = 'overview' | 'news' | 'wallStreet' | 'social' | 'calendar' | 'portfolio' | 'signals' | 'alerts' | 'whales' | 'ai' | 'profile' | 'settings';
export type PageMeta = { id: PageId; path: string; readonly title: string; group: 'account' | 'overview' | 'markets' | 'calendar' | 'assets' | 'ai'; icon: string };

const page = (id: PageId, path: string, group: PageMeta['group'], icon: string): PageMeta => ({
  id, path, group, icon,
  get title() { return i18n.t(`nav:page.${id}`); },
});

export const PAGES = {
  overview: page('overview', '/', 'overview', 'home'),
  news: page('news', '/news', 'markets', 'news'),
  wallStreet: page('wallStreet', '/wall-street', 'markets', 'ws'),
  social: page('social', '/social', 'markets', 'radar'),
  calendar: page('calendar', '/calendar', 'calendar', 'cal'),
  portfolio: page('portfolio', '/portfolio', 'assets', 'pie'),
  signals: page('signals', '/signals', 'assets', 'link'),
  alerts: page('alerts', '/alerts', 'assets', 'price'),
  whales: page('whales', '/whales', 'assets', 'whale'),
  ai: page('ai', '/ai', 'ai', 'ai'),
  profile: page('profile', '/profile', 'account', 'user'),
  settings: page('settings', '/settings', 'account', 'gear'),
} satisfies Record<string, PageMeta>;

/** [group, dictionary key nav:group.<key>, path] */
export const GROUPS: Array<[PageMeta['group'], 'overview' | 'markets' | 'calendar' | 'assets' | 'ai', string]> = [
  ['overview', 'overview', '/'],
  ['markets', 'markets', '/news'],
  ['calendar', 'calendar', '/calendar'],
  ['assets', 'assets', '/portfolio'],
  ['ai', 'ai', '/ai'],
];

export const SUB_TABS: Partial<Record<PageMeta['group'], PageMeta[]>> = {
  markets: [PAGES.news, PAGES.wallStreet, PAGES.social],
  assets: [PAGES.portfolio, PAGES.signals, PAGES.alerts, PAGES.whales],
  account: [PAGES.profile, PAGES.settings],
};

/** [dictionary key nav:drawer.<key>, pages] */
export const DRAWER: Array<['overview' | 'markets' | 'calendar' | 'assets' | 'assistant' | 'account', PageMeta[]]> = [
  ['overview', [PAGES.overview]],
  ['markets', [PAGES.news, PAGES.wallStreet, PAGES.social]],
  ['calendar', [PAGES.calendar]],
  ['assets', [PAGES.portfolio, PAGES.signals, PAGES.alerts, PAGES.whales]],
  ['assistant', [PAGES.ai]],
  ['account', [PAGES.profile, PAGES.settings]],
];

export const pageByPath = (path: string): PageMeta =>
  Object.values(PAGES).find((p) => p.path === path) ?? PAGES.overview;
